import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Clock, Pencil, X } from "lucide-react";
import { Button, Input, Typography } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { EMPTY_STAY_SCHEDULE_DEFAULTS, useSaveStayScheduleDefaults, useStayScheduleDefaults, type StayScheduleDefaults, type StayShiftMode } from "@/hooks/useStayScheduleDefaults";
import { SectionCard, SectionHeader } from "./primitives";

type Props = {
  hasDaycare: boolean;
  hasHotel: boolean;
  canEditDaycare: boolean;
  canEditHotel: boolean;
};

export default function StayScheduleDefaultsSection({ hasDaycare, hasHotel, canEditDaycare, canEditHotel }: Props) {
  const { t } = useTranslation("daycareHotel");
  const { data: config, isLoading } = useStayScheduleDefaults();
  const save = useSaveStayScheduleDefaults();
  const [draft, setDraft] = useState<StayScheduleDefaults | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const current = draft ?? config ?? EMPTY_STAY_SCHEDULE_DEFAULTS;
  const canEdit = (hasDaycare && canEditDaycare) || (hasHotel && canEditHotel);

  useEffect(() => {
    if (config && !isEditing) setDraft(config);
  }, [config, isEditing]);

  if (isLoading && !config) return null;

  const update = (patch: Partial<StayScheduleDefaults>) => setDraft({ ...current, ...patch });
  const saveEditing = async () => {
    try {
      await save.mutateAsync({
        ...(hasDaycare && canEditDaycare ? {
          daycareCheckInFrom: current.daycareCheckInFrom,
          daycareCheckInTo: current.daycareCheckInTo,
          daycareCheckOutFrom: current.daycareCheckOutFrom,
          daycareCheckOutTo: current.daycareCheckOutTo,
          daycareShiftMode: current.daycareShiftMode,
          daycareEveningCheckInFrom: current.daycareEveningCheckInFrom,
          daycareEveningCheckInTo: current.daycareEveningCheckInTo,
          daycareEveningCheckOutFrom: current.daycareEveningCheckOutFrom,
          daycareEveningCheckOutTo: current.daycareEveningCheckOutTo,
        } : {}),
        ...(hasHotel && canEditHotel ? {
          hotelCheckInFrom: current.hotelCheckInFrom,
          hotelCheckInTo: current.hotelCheckInTo,
          hotelCheckOutFrom: current.hotelCheckOutFrom,
          hotelCheckOutTo: current.hotelCheckOutTo,
          hotelShiftMode: current.hotelShiftMode,
          hotelEveningCheckInFrom: current.hotelEveningCheckInFrom,
          hotelEveningCheckInTo: current.hotelEveningCheckInTo,
          hotelEveningCheckOutFrom: current.hotelEveningCheckOutFrom,
          hotelEveningCheckOutTo: current.hotelEveningCheckOutTo,
        } : {}),
      });
      setIsEditing(false);
      toast({ title: t("scheduleDefaults.toastUpdated") });
    } catch {
      toast({ variant: "destructive", title: t("scheduleDefaults.toastSaveFailed") });
    }
  };

  const card = (module: "daycare" | "hotel") => {
    const isDaycare = module === "daycare";
    const enabled = isDaycare ? hasDaycare : hasHotel;
    const editable = isDaycare ? canEditDaycare : canEditHotel;
    if (!enabled) return null;
    const fields = isDaycare
      ? { checkInFrom: "daycareCheckInFrom", checkInTo: "daycareCheckInTo", checkOutFrom: "daycareCheckOutFrom", checkOutTo: "daycareCheckOutTo" }
      : { checkInFrom: "hotelCheckInFrom", checkInTo: "hotelCheckInTo", checkOutFrom: "hotelCheckOutFrom", checkOutTo: "hotelCheckOutTo" };
    const eveningFields = isDaycare
      ? { checkInFrom: "daycareEveningCheckInFrom", checkInTo: "daycareEveningCheckInTo", checkOutFrom: "daycareEveningCheckOutFrom", checkOutTo: "daycareEveningCheckOutTo" }
      : { checkInFrom: "hotelEveningCheckInFrom", checkInTo: "hotelEveningCheckInTo", checkOutFrom: "hotelEveningCheckOutFrom", checkOutTo: "hotelEveningCheckOutTo" };
    const modeKey = (isDaycare ? "daycareShiftMode" : "hotelShiftMode") as keyof StayScheduleDefaults;
    const mode = current[modeKey] as StayShiftMode;
    const timeField = (field: keyof typeof fields, group: typeof fields = fields) => {
      const key = group[field] as keyof StayScheduleDefaults;
      return (
        <label className="grid gap-1.5 text-sm text-muted-foreground">
          {t(`scheduleDefaults.${module}.${field}`)}
          <Input
            type="time"
            aria-label={`${t(`scheduleDefaults.${module}.${field}`)}${group === fields ? "" : ` · ${t("scheduleDefaults.evening")}`}`}
            value={current[key] as string}
            disabled={!isEditing || !editable}
            onChange={(event) => update({ [key]: event.target.value })}
          />
        </label>
      );
    };
    return (
      <SectionCard key={module} className="grid content-start gap-3 p-4">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Typography asChild variant="h3">
            <span className="text-sm font-semibold text-foreground">{t(`scheduleDefaults.${module}.title`)}</span>
          </Typography>
        </div>
        <div className="grid gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">{t("scheduleDefaults.shiftModeLabel")}</span>
          <div role="radiogroup" aria-label={`${t("scheduleDefaults.shiftModeLabel")} · ${t(`scheduleDefaults.${module}.title`)}`} className="flex flex-wrap gap-1.5">
            {(["single", "two"] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={mode === value}
                disabled={!isEditing || !editable}
                onClick={() => update({ [modeKey]: value })}
                className={`rounded-full border px-3 py-1 text-sm font-medium disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${mode === value ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-foreground hover:bg-muted"}`}
              >
                {t(value === "two" ? "scheduleDefaults.shiftTwo" : "scheduleDefaults.shiftSingle")}
              </button>
            ))}
          </div>
          {isEditing ? <span className="text-xs text-color-light">{t("scheduleDefaults.shiftHint")}</span> : null}
        </div>

        {mode === "two" ? <span className="text-xs font-semibold uppercase tracking-wide text-color-light">{t("scheduleDefaults.morning")}</span> : null}
        <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label={t(`scheduleDefaults.${module}.checkIn`)}>
          {timeField("checkInFrom")}
          {timeField("checkInTo")}
        </div>
        <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label={t(`scheduleDefaults.${module}.checkOut`)}>
          {timeField("checkOutFrom")}
          {timeField("checkOutTo")}
        </div>

        {mode === "two" ? (
          <>
            <span className="text-xs font-semibold uppercase tracking-wide text-color-light">{t("scheduleDefaults.evening")}</span>
            <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label={`${t(`scheduleDefaults.${module}.checkIn`)} · ${t("scheduleDefaults.evening")}`}>
              {timeField("checkInFrom", eveningFields)}
              {timeField("checkInTo", eveningFields)}
            </div>
            <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label={`${t(`scheduleDefaults.${module}.checkOut`)} · ${t("scheduleDefaults.evening")}`}>
              {timeField("checkOutFrom", eveningFields)}
              {timeField("checkOutTo", eveningFields)}
            </div>
          </>
        ) : null}
      </SectionCard>
    );
  };

  return (
    <section>
      <SectionHeader
        tone="daycare"
        badge={t("scheduleDefaults.badge")}
        title={t("scheduleDefaults.title")}
        description={t("scheduleDefaults.description")}
        action={canEdit && !isEditing ? (
          <Button type="button" size="sm" variant="outline" onClick={() => { setDraft(current); setIsEditing(true); }}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t("common.edit")}
          </Button>
        ) : null}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {card("daycare")}
        {card("hotel")}
      </div>
      {isEditing ? (
        <div className="mt-3 flex justify-end gap-2">
          <Button type="button" size="sm" variant="outline" disabled={save.isPending} onClick={() => { setDraft(config ?? null); setIsEditing(false); }}>
            <X className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t("common.cancel")}
          </Button>
          <Button type="button" size="sm" disabled={save.isPending} onClick={saveEditing}>
            <Check className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
